// Angular
import { Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { NgClass, KeyValuePipe, TitleCasePipe } from '@angular/common';
import { FormGroup, FormControl, Validators, ReactiveFormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Angular Material
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatButtonModule } from '@angular/material/button';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatCardModule } from '@angular/material/card';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { MatTooltipModule } from '@angular/material/tooltip';

// Third Party
import { debounceTime, finalize, tap } from 'rxjs';

// Services
import { CustomerService } from '@services/customer.service';
import { UserManagementService } from '@services/user-management.service';

// Models
import { Stack } from '@app/defs/stack';

// Constants
import { Action, YesNo } from '@constants/enum';
import { DEBOUNCE_TIME_SEARCH_FIELDS } from '@constants/common';

// Dialogs
import { LoadingDialogComponent, SuccessDialogComponent } from '@components/dialogs';

// Components
import { LoadingSpinnerComponent } from '@components/common-components';
import { Customer } from '@components/customer';

@Component({
  templateUrl: './account-manager-form.component.html',
  styleUrl: './account-manager-form.component.scss',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatButtonModule,
    MatCheckboxModule,
    MatCardModule,
    MatSidenavModule,
    MatTooltipModule,
    RouterLink,
    NgClass,
    KeyValuePipe,
    TitleCasePipe,
    LoadingSpinnerComponent,
  ],
})
export class AccountManagerFormComponent implements OnInit {
  private customerService = inject(CustomerService);
  private userManagementService = inject(UserManagementService);
  private dialog = inject(MatDialog);
  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  readonly Action = Action;

  readonly type = signal<string>(null);

  readonly stacks = signal<Stack[]>(null);

  readonly defaultEmail = signal('');
  readonly defaultCustomers = signal<string[]>([]);

  readonly uniqueCustomerEmail = signal(true);

  readonly loading = signal(true);

  readonly managerForm = new FormGroup({
    firstName: new FormControl('', [Validators.required]),
    lastName: new FormControl('', [Validators.required]),
    email: new FormControl('', [Validators.required, Validators.email]),
    customers: new FormControl([], [Validators.required, this.maxArrayLength(100)]),
    enabled: new FormControl(true),
    guid: new FormControl(''),
    // TODO - When BI is to be selectable for Account Managers
    // businessIntelligenceAccess: new FormControl(false),
  });

  readonly selectedList = signal<Map<string, string>>(new Map());
  readonly deselectedList = signal<Map<string, string>>(new Map());
  readonly originalSelect = signal<Map<string, string>>(new Map());
  readonly originalDeselect = signal<Map<string, string>>(new Map());
  readonly deselectedListToBeMoved = signal<Map<string, string>>(new Map());
  readonly selectedListToBeMoved = signal<Map<string, string>>(new Map());

  readonly statusList = Object.entries(YesNo).map(([name, value]) => ({ name, value }));

  ngOnInit() {
    const kcGuid = history.state?.kcGuid;
    this.type.set(kcGuid ? Action.edit : Action.create);
    if (this.type() === Action.edit) {
      this.userManagementService
        .getAccountManager(kcGuid)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (data) => {
            const manager = data['entity'];
            this.managerForm.patchValue({
              firstName: manager.firstName,
              lastName: manager.lastName,
              email: manager.email,
              customers: manager.customers,
              enabled: manager.enabled,
              guid: manager.guid,
              // TODO - When BI is to be selectable for Account Managers
              // businessIntelligenceAccess: this.manager.businessIntelligenceAccess,
            });
            this.defaultEmail.set(manager.email);
            this.defaultCustomers.set(manager.customers);
            this.getAllCustomers();
          },
          error: () => {},
        });
    } else {
      this.getAllCustomers();
    }

    this.managerForm
      .get('email')
      .valueChanges.pipe(
        debounceTime(DEBOUNCE_TIME_SEARCH_FIELDS),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.checkCustomerEmail());
  }

  onSubmit() {
    if (this.uniqueCustomerEmail() && this.managerForm.valid) {
      this.loading.set(true);
      const customerDifferenceCount = this.compareCustomers(
        this.managerForm.get('customers').value,
        this.defaultCustomers(),
      );
      let loadingRef = null;
      if (customerDifferenceCount > 30) {
        loadingRef = this.openLoadingDialog(customerDifferenceCount);
      }

      const request =
        this.type() === Action.create
          ? this.userManagementService.createManager(JSON.stringify(this.managerForm.value))
          : this.userManagementService.editmanager(JSON.stringify(this.managerForm.value));

      request.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: () => {
          loadingRef?.close();
          this.loading.set(false);
          this.openSuccessDialog(this.managerForm.get('email').value);
        },
        error: () => {
          loadingRef?.close();
          this.loading.set(false);
        },
      });
    } else {
      this.managerForm.markAllAsTouched();
    }
  }

  checkCustomerEmail() {
    // empty email string causes error
    const email = this.managerForm.get('email').value;
    if (email) {
      this.userManagementService
        .checkEmail(email)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((data) => {
          // Back end search is case sensitive.
          if (data['entity'] && email.toLowerCase() != this.defaultEmail().toLowerCase()) {
            this.uniqueCustomerEmail.set(false);
          } else {
            this.uniqueCustomerEmail.set(true);
          }
        });
    }
  }

  getErrorMessage(name: string) {
    switch (name) {
      case 'firstName': {
        if (
          this.managerForm.controls['firstName'].hasError('required') &&
          this.managerForm.controls['firstName'].touched
        ) {
          return 'You must enter a first name.';
        }
        return 'Error';
      }
      case 'lastName': {
        if (
          this.managerForm.controls['lastName'].hasError('required') &&
          this.managerForm.controls['lastName'].touched
        ) {
          return 'You must enter a last name.';
        }
        return 'Error';
      }
      case 'email': {
        if (!this.uniqueCustomerEmail()) {
          return 'A user already exists with that email.';
        }
        if (
          this.managerForm.controls['email'].hasError('required') &&
          this.managerForm.controls['email'].touched
        ) {
          return 'You must enter an email.';
        }
        if (
          this.managerForm.controls['email'].hasError('email') &&
          this.managerForm.controls['email'].touched
        ) {
          return 'You must enter a valid email.';
        }
        return 'Error';
      }
      case 'customers': {
        if (this.managerForm.controls['customers'].hasError('required')) {
          return 'You must select at least one customer.';
        }
        if (this.managerForm.get('customers').hasError('tooMany')) {
          return 'Cannot assign more than 100 Customers.';
        }
      }
    }
    return null;
  }

  getAllCustomers() {
    this.customerService
      .getTenantList()
      .pipe(
        tap((data) => {
          this.splitCustomerList(data);
        }),
        finalize(() => this.loading.set(false)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }

  // Preserves the existing order.
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  asIsOrder(a, b) {
    return 1;
  }

  splitCustomerList(customers: Customer[]) {
    const selected = new Map<string, string>();
    const deselected = new Map<string, string>();

    for (const customer of customers) {
      if (this.managerForm.get('customers').value.includes(customer.tenantKey)) {
        selected.set(customer.name, customer.tenantKey);
      } else if (customer.status !== 0) {
        deselected.set(customer.name, customer.tenantKey);
      }
    }

    const sortMap = (map: Map<string, string>) =>
      new Map(
        [...map].sort((a, b) => String(a[0].toLowerCase()).localeCompare(b[0].toLowerCase())),
      );

    this.selectedList.set(sortMap(selected));
    this.originalSelect.set(sortMap(selected));
    this.deselectedList.set(sortMap(deselected));
    this.originalDeselect.set(sortMap(deselected));
  }

  selectCheck(event: boolean, key: string, value: string) {
    this.selectedListToBeMoved.update((map) => {
      const newMap = new Map(map);
      if (event) {
        newMap.set(key, value);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  }

  deselectCheck(event: boolean, key: string, value: string) {
    this.deselectedListToBeMoved.update((map) => {
      const newMap = new Map(map);
      if (event) {
        newMap.set(key, value);
      } else {
        newMap.delete(key);
      }
      return newMap;
    });
  }

  selectCustomer() {
    const sortMap = (map: Map<string, string>) =>
      new Map(
        [...map].sort((a, b) => String(a[0].toLowerCase()).localeCompare(b[0].toLowerCase())),
      );

    const newSelected = new Map(this.originalSelect());
    const newDeselected = new Map(this.originalDeselect());
    const newDeselectedView = new Map(this.deselectedList());

    for (const [key, value] of this.deselectedListToBeMoved()) {
      newDeselected.delete(key);
      newDeselectedView.delete(key);
      newSelected.set(key, value);
    }

    this.originalSelect.set(sortMap(newSelected));
    this.selectedList.set(sortMap(newSelected));
    this.originalDeselect.set(sortMap(newDeselected));
    this.deselectedList.set(sortMap(newDeselectedView));

    const data = Array.from(this.originalSelect().values());
    this.managerForm.get('customers').setValue(data);

    this.deselectedListToBeMoved.set(new Map());
  }

  deselectCustomer() {
    const sortMap = (map: Map<string, string>) =>
      new Map(
        [...map].sort((a, b) => String(a[0].toLowerCase()).localeCompare(b[0].toLowerCase())),
      );

    const newSelected = new Map(this.originalSelect());
    const newDeselected = new Map(this.originalDeselect());
    const newSelectedView = new Map(this.selectedList());

    for (const [key, value] of this.selectedListToBeMoved()) {
      newSelected.delete(key);
      newSelectedView.delete(key);
      newDeselected.set(key, value);
    }

    this.originalSelect.set(sortMap(newSelected));
    this.selectedList.set(sortMap(newSelectedView));
    this.originalDeselect.set(sortMap(newDeselected));
    this.deselectedList.set(sortMap(newDeselected));

    const data = Array.from(this.originalSelect().values());
    this.managerForm.get('customers').setValue(data);

    this.selectedListToBeMoved.set(new Map());
  }

  searchSelected(search: string) {
    this.selectedList.set(
      new Map(
        [...this.originalSelect()].filter(([key]) =>
          key.toLowerCase().includes(search.toLowerCase()),
        ),
      ),
    );
  }

  searchDeselected(search: string) {
    this.deselectedList.set(
      new Map(
        [...this.originalDeselect()].filter(([key]) =>
          key.toLowerCase().includes(search.toLowerCase()),
        ),
      ),
    );
  }

  maxArrayLength(max: number) {
    return (control: FormControl) => {
      return control.value?.length <= max ? null : { tooMany: true };
    };
  }

  openLoadingDialog(customerCount: number): MatDialogRef<LoadingDialogComponent> {
    return this.dialog.open(LoadingDialogComponent, {
      data: { customerCount },
      disableClose: true,
    });
  }

  openSuccessDialog(email: string) {
    const ref = this.dialog.open(SuccessDialogComponent, {
      data: {
        title: `Updated!`,
        message: `You have successfully updated the account manager: ${email}.`,
        button: 'Great!',
      },
    });
    ref
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.router.navigate(['/managers']);
      });
  }

  compareCustomers(selectedCustomers: string[], defaultCustomers: string[]) {
    // Count customers in the new list that weren't in the original list (additions)
    const added = selectedCustomers.filter((c) => !defaultCustomers.includes(c)).length;
    // Count customers in the original list that aren't in the new list (removals)
    const removed = defaultCustomers.filter((c) => !selectedCustomers.includes(c)).length;
    return added + removed;
  }
}
